/* ==========================================================================
   cl-float: floating labels on every field of the theme (Gabriel 2026-10-10).

   The label rests inside the field, vertically centred and aligned with the
   text; on focus, or once the field holds a value, it rises onto the top
   border, 12px, on a notch that hides the border behind it. Selects and date
   fields keep it up; a textarea rests it on its first line.

   One script for every form family, because they share no markup:
   - theme fields (.cl-field, cart, search, footer, contact form, quiz),
   - HubSpot v2 forms rendered inline (stega-hs-modal, cl-bf-modal, legacy
     cl-hubspot-form panes). HubSpot's React owns their class lists, so the
     script only writes data attributes and custom properties, never classes.

   How a field is found: every text-like control inside .cl, #stega-hsm,
   #cl-bfm or .hs-form. Its label is its own <label for> when that label is
   visible and separate; otherwise a span is made from the placeholder (or the
   visually-hidden label, or aria-label) and marked aria-hidden, since the
   accessible name is already there. A label that wraps a select together with
   its own text (sort by) is left alone, as are qty steppers (type=number) and
   anything inside [data-cl-fl-off].

   Geometry is measured, not assumed: the label is absolutely placed in the
   nearest common ancestor of label and control, at the control's text inset
   (--fl-x), the centre or first line (--fl-y) and the top border (--fl-uy) of
   the "frame", the control itself or, for a borderless input inside a
   bordered box (footer newsletter), that box. A ResizeObserver re-measures.
   The notch is two colours, the frame's outside above the border and its
   inside below, both composited from the real backgrounds (an ancestor can
   force one with --cl-fl-notch). The styles live in cl-base.css.
   ========================================================================== */
(function () {
  'use strict';
  if (window.clFloat) return;
  var SCOPE = '.cl, #stega-hsm, #cl-bfm, .hs-form';
  var CTRL = 'input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([type=submit]):not([type=button]):not([type=reset]):not([type=image]):not([type=range]):not([type=file]):not([type=color]):not([type=number]),textarea,select';
  var ALWAYS_UP = /^(date|time|datetime-local|month|week)$/;
  var recs = new WeakMap();
  var ro = 'ResizeObserver' in window ? new ResizeObserver(function (list) {
    list.forEach(function (en) {
      var r = recs.get(en.target);
      (Array.isArray(r) ? r : r ? [r] : []).forEach(function (x) { if (x.ctrl.isConnected) place(x); });
    });
  }) : null;

  function txt(el) { return (el && el.textContent || '').replace(/\s+/g, ' ').trim(); }
  /* the words a wrapping label carries itself: not the control's, a button's (search clear) or hidden text */
  function ownText(l, ctrl) {
    var out = '', w = document.createTreeWalker(l, NodeFilter.SHOW_TEXT);
    for (var n = w.nextNode(); n; n = w.nextNode()) {
      var p = n.parentElement;
      if (ctrl.contains(n) || (p && p.closest('button, select, [aria-hidden="true"], [hidden], svg'))) continue;
      out += n.nodeValue;
    }
    return out.replace(/\s+/g, ' ').trim();
  }
  function srOnly(l) {
    if (/(^|\s)(visually-hidden|sr-only|visuallyhidden|screen-reader-text)(\s|$)/.test(l.className || '')) return true;
    var cs = getComputedStyle(l);
    if (cs.display === 'none' || cs.visibility === 'hidden') return true;
    if (/rect\(0/.test(cs.clip) || cs.clipPath === 'inset(50%)') return true;
    return l.offsetWidth > 0 && l.offsetWidth <= 2 && l.offsetHeight <= 2;
  }
  function rgba(c) {
    var m = /rgba?\(([^)]+)\)/.exec(c || '');
    if (!m) return [0, 0, 0, 0];
    var p = m[1].split(/[\s,\/]+/).filter(Boolean).map(parseFloat);
    return [p[0], p[1], p[2], p.length > 3 ? p[3] : 1];
  }
  /* The solid colour an element shows: its background over its ancestors', alpha composited. */
  function surface(el) {
    var layers = [];
    for (var n = el; n && n.nodeType === 1; n = n.parentElement) {
      var c = rgba(getComputedStyle(n).backgroundColor);
      if (c[3] > 0) { layers.push(c); if (c[3] >= 1) break; }
    }
    var out = [255, 255, 255];
    for (var i = layers.length - 1; i >= 0; i--) {
      var a = layers[i][3];
      out = [0, 1, 2].map(function (k) { return layers[i][k] * a + out[k] * (1 - a); });
    }
    return 'rgb(' + out.map(Math.round).join(',') + ')';
  }
  function bordered(el) {
    var cs = getComputedStyle(el);
    return ['Top', 'Bottom'].some(function (s) { return parseFloat(cs['border' + s + 'Width']) > 0 && rgba(cs['border' + s + 'Color'])[3] > 0; });
  }
  /* The box that draws the field: the control, or a bordered wrapper close above it that holds no other field. */
  function frameOf(ctrl, box) {
    if (bordered(ctrl)) return ctrl;
    var n = ctrl.parentElement;
    for (var i = 0; i < 3 && n && n !== box.parentElement; i++, n = n.parentElement) {
      if (n.querySelectorAll(CTRL).length > 1) break;
      if (bordered(n)) return n;
    }
    return ctrl;
  }
  function common(a, b) {
    for (var n = a; n; n = n.parentElement) if (n.contains(b)) return n;
    return a.parentElement;
  }

  function setup(ctrl) {
    if (recs.has(ctrl) || ctrl.closest('[data-cl-fl-off]') || !ctrl.closest(SCOPE)) return;
    var labels = ctrl.labels ? Array.prototype.slice.call(ctrl.labels) : [];
    var label = null, text = '', wraps = false;
    labels.forEach(function (l) {
      if (l.contains(ctrl)) {
        wraps = true;
        var own = ownText(l, ctrl);
        if (own && !srOnly(l)) wraps = 'text';
      } else if (!label && txt(l) && !srOnly(l)) label = l;
    });
    if (wraps === 'text' && !label) return; // "Sort by <select>": its words already sit beside it
    var ph = (ctrl.getAttribute('placeholder') || '').trim();
    if (!label) {
      var hidden = labels.filter(function (l) { return !l.contains(ctrl) && txt(l); })[0];
      /* a select named only by aria-label is a compact picker (split cards' size), not a form field */
      if (ctrl.tagName === 'SELECT' && !hidden) return;
      text = ph || (hidden && txt(hidden)) || (ctrl.getAttribute('aria-label') || '').trim();
      if (!text) return;
    }
    var rec = { ctrl: ctrl, label: label, synth: !label };
    if (!label) {
      label = document.createElement('span');
      label.setAttribute('aria-hidden', 'true');
      label.textContent = text;
      ctrl.insertAdjacentElement('afterend', label);
      rec.label = label;
    }
    rec.box = common(label, ctrl);
    var dup = ph && ph.toLowerCase() === txt(label).replace(/\*$/, '').trim().toLowerCase();
    if (rec.synth || dup) rec.dup = true;
    rec.kind = ctrl.tagName === 'SELECT' ? 'select' : ctrl.tagName === 'TEXTAREA' ? 'area' : 'input';
    recs.set(ctrl, rec);
    var onBox = recs.get(rec.box);
    recs.set(rec.box, (Array.isArray(onBox) ? onBox : []).concat(rec));
    label.setAttribute('data-cl-fl-label', '');
    ctrl.setAttribute('data-cl-fl-ctrl', '');
    if (getComputedStyle(rec.box).position === 'static') rec.box.setAttribute('data-cl-fl-rel', '');
    if (ro) { ro.observe(ctrl); ro.observe(rec.box); }
    place(rec);
    state(rec);
    /* values that arrive without an input event: HubSpot's known-contact prefill, restored form state */
    [250, 1000, 3000].forEach(function (t) { setTimeout(function () { state(rec); }, t); });
  }

  function place(rec) {
    var c = rec.ctrl, l = rec.label, box = rec.box;
    var cr = c.getBoundingClientRect();
    if (!cr.width || !cr.height) return; // in a closed drawer: the observer calls again once it shows
    var frame = rec.frame && rec.frame.isConnected ? rec.frame : (rec.frame = frameOf(c, box));
    if (frame !== c && !frame.hasAttribute('data-cl-fl-frame')) frame.setAttribute('data-cl-fl-frame', '');
    if (frame === c) c.setAttribute('data-cl-fl-frame', '');
    var br = box.getBoundingClientRect(), fr = frame.getBoundingClientRect();
    var cs = getComputedStyle(c), fs = parseFloat(cs.fontSize) || 16;
    var ox = br.left + box.clientLeft, oy = br.top + box.clientTop;
    var padL = parseFloat(cs.paddingLeft) + parseFloat(cs.borderLeftWidth);
    var padR = parseFloat(cs.paddingRight) + parseFloat(cs.borderRightWidth);
    var x = cr.left - ox + padL - 4;
    var y;
    if (rec.kind === 'area') {
      var lh = parseFloat(cs.lineHeight) || fs * 1.4;
      y = cr.top - oy + parseFloat(cs.borderTopWidth) + parseFloat(cs.paddingTop) + lh / 2;
    } else {
      y = cr.top - oy + cr.height / 2;
    }
    var fcs = getComputedStyle(frame);
    var uy = fr.top - oy + parseFloat(fcs.borderTopWidth) / 2;
    var notch = (getComputedStyle(c).getPropertyValue('--cl-fl-notch') || '').trim();
    var inside = notch || surface(frame);
    var outside = notch || (frame.parentElement ? surface(frame.parentElement) : inside);
    var ink = rgba(cs.color);
    var s = l.style;
    s.setProperty('--fl-x', x.toFixed(1) + 'px');
    s.setProperty('--fl-y', y.toFixed(1) + 'px');
    s.setProperty('--fl-uy', uy.toFixed(1) + 'px');
    s.setProperty('--fl-w', Math.max(40, cr.width - padL - padR + 8).toFixed(0) + 'px');
    s.setProperty('--fl-fs', fs + 'px');
    s.setProperty('--fl-k', (Math.min(12, fs) / fs).toFixed(3));
    s.setProperty('--fl-in', inside);
    s.setProperty('--fl-out', outside);
    s.setProperty('--fl-ink', 'rgb(' + ink.slice(0, 3).join(',') + ')');
    s.setProperty('--fl-mute', 'rgba(' + ink.slice(0, 3).join(',') + ',.62)');
    if (!rec.placed) {
      rec.placed = true;
      /* transitions only after the first placement, so nothing slides in on load */
      requestAnimationFrame(function () { requestAnimationFrame(function () { rec.anim = true; state(rec); }); });
    }
  }

  function autofilled(c) {
    try { if (c.matches(':autofill')) return true; } catch (e) { /* older engines */ }
    try { return c.matches(':-webkit-autofill'); } catch (e) { return false; }
  }
  function state(rec) {
    var c = rec.ctrl;
    if (!c.isConnected) return;
    var focus = c.ownerDocument.activeElement === c;
    var filled = rec.kind === 'select' || ALWAYS_UP.test(c.type) || c.value !== '' || autofilled(c);
    var t = ['on'];
    if (focus) t.push('focus');
    if (filled) t.push('filled');
    if (focus || filled) t.push('up');
    if (rec.kind === 'area') t.push('area');
    if (rec.anim) t.push('anim');
    var v = t.join(' ');
    if (rec.label.getAttribute('data-cl-fl-label') !== v) rec.label.setAttribute('data-cl-fl-label', v);
    var cv = t.concat(rec.dup ? ['dup'] : []).join(' ');
    if (c.getAttribute('data-cl-fl-ctrl') !== cv) c.setAttribute('data-cl-fl-ctrl', cv);
    if (!rec.placed) place(rec);
  }

  function scan(root) {
    if (!root || root.nodeType !== 1 && root.nodeType !== 9) return;
    if (root.nodeType === 1 && root.matches(CTRL)) setup(root);
    if (root.querySelectorAll) Array.prototype.forEach.call(root.querySelectorAll(CTRL), setup);
  }
  function onEvent(e) {
    var r = e.target && recs.get(e.target);
    if (r && !Array.isArray(r)) state(r);
  }
  ['focusin', 'focusout', 'input', 'change'].forEach(function (n) { document.addEventListener(n, onEvent, true); });
  /* autofill: the keyframes in cl-base.css start when the browser fills a field */
  document.addEventListener('animationstart', function (e) { if (/^cl-fl-af/.test(e.animationName)) onEvent(e); }, true);
  document.addEventListener('reset', function (e) {
    setTimeout(function () { Array.prototype.forEach.call(e.target.querySelectorAll('[data-cl-fl-ctrl]'), function (c) { var r = recs.get(c); if (r) state(r); }); }, 0);
  }, true);

  var queued = false, pending = [];
  new MutationObserver(function (list) {
    for (var i = 0; i < list.length; i++) for (var j = 0; j < list[i].addedNodes.length; j++) {
      var n = list[i].addedNodes[j];
      if (n.nodeType === 1 && !n.hasAttribute('data-cl-fl-label')) pending.push(n);
    }
    if (pending.length && !queued) {
      queued = true;
      requestAnimationFrame(function () { queued = false; var p = pending; pending = []; p.forEach(function (n) { if (n.isConnected) scan(n); }); });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  function all() { scan(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all); else all();
  function replaceAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cl-fl-ctrl]'), function (c) { var r = recs.get(c); if (r) place(r); });
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(replaceAll);
  window.addEventListener('load', replaceAll);
  if (!ro) window.addEventListener('resize', replaceAll);

  window.clFloat = { scan: scan, refresh: replaceAll };
})();

/* --------------------------------------------------------------------------
   HubSpot meetings height (book-a-call card, article and LP scheduler bands).
   Here because this file loads on every page. HubSpot's MeetingsEmbedCode.js
   resizes its iframe from the frame's {height} messages, but only for the
   meetings / meetings-eu1 origins: the ANZ scheduler is meetings-ap1, so the
   frame stayed at the script's fixed 756px whatever the step, with dead space
   under the calendar. Same rule, any hublet.
   -------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.clMeetingsResize) return;
  window.clMeetingsResize = true;
  window.addEventListener('message', function (e) {
    if (!/^https:\/\/meetings(-[a-z0-9]+)?\.hubspot\.com$/.test(e.origin)) return;
    var h = e.data && e.data.height;
    if (typeof h !== 'number' || h < 200 || h > 4000) return;
    var frames = document.querySelectorAll('.meetings-iframe-container iframe');
    for (var i = 0; i < frames.length; i++) {
      if (frames[i].contentWindow === e.source) { frames[i].style.height = h + 'px'; return; }
    }
  });
})();
